import { Table as MantineTable, ScrollArea } from '@mantine/core'
import {
  IllustratedMessage,
  Loader,
  TableCell,
  TableHeader,
  TableRow,
} from '@percent/lemonade'
import type { ReactNode } from 'react'
import styled, { FlattenSimpleInterpolation } from 'styled-components'
import { useTableContext } from './context/table-provider'
import { useColumnStyles } from './hooks/use-column-styles'

const StyledScrollArea = styled(ScrollArea)<{
  $totalStickyWidth: number
}>`
  ${({ $totalStickyWidth }) =>
    $totalStickyWidth > 0
      ? `
    .mantine-ScrollArea-scrollbar[data-orientation="horizontal"] {
      z-index: 3;
      left: ${$totalStickyWidth}px;
      width: calc(100% - ${$totalStickyWidth}px);
    }
  `
      : ''}
`

export const StyledTableContainer = styled(MantineTable)<{
  $stickyStyles: FlattenSimpleInterpolation[]
  $disableHorizontalScroll?: boolean
}>`
  width: ${({ $disableHorizontalScroll }) =>
    $disableHorizontalScroll ? '100%' : 'max-content'};
  min-width: 100%;
  ${({ $stickyStyles }) => $stickyStyles}
`

const StyledNoResultsWrapper = styled.div`
  ${({ theme }) =>
    `
      margin-top: ${theme.sizes.xxl};
      margin-bottom: ${theme.sizes.xxl};
      word-break: break-word;
      overflow-wrap: break-word;
      white-space: normal;
    `}
`

const StyledLoadingWrapper = styled.div`
  padding: 100px 0;
`

export function Table({ children }: { children: ReactNode }) {
  const {
    isLoading,
    totalResults,
    illustration,
    emptyTableTitle,
    emptyTableDescription,
    emptyTableButton,
    emptyTableButtonShape,
    columns,
    disableHorizontalScroll,
  } = useTableContext()

  const { styles: stickyStyles, totalStickyWidth } = useColumnStyles(columns)

  if (isLoading) {
    return (
      <StyledTableContainer $stickyStyles={stickyStyles}>
        <TableHeader />
        <MantineTable.Tbody>
          <TableRow>
            <TableCell colSpan={columns.length}>
              <StyledLoadingWrapper>
                <Loader isFullScreen={false} />
              </StyledLoadingWrapper>
            </TableCell>
          </TableRow>
        </MantineTable.Tbody>
      </StyledTableContainer>
    )
  }

  if (totalResults === 0) {
    return (
      <StyledTableContainer $stickyStyles={stickyStyles}>
        <StyledNoResultsWrapper>
          <IllustratedMessage
            illustration={illustration}
            title={emptyTableTitle}
            description={emptyTableDescription}
            cta={emptyTableButton}
            ctaShape={emptyTableButtonShape}
          />
        </StyledNoResultsWrapper>
      </StyledTableContainer>
    )
  }

  const inner = (
    <StyledTableContainer
      $stickyStyles={stickyStyles}
      $disableHorizontalScroll={disableHorizontalScroll}
    >
      <TableHeader />
      {children}
    </StyledTableContainer>
  )

  if (disableHorizontalScroll) return inner

  return (
    <StyledScrollArea type="hover" $totalStickyWidth={totalStickyWidth}>
      {inner}
    </StyledScrollArea>
  )
}
